import { newSnowDensity } from '../sim/precipitation.ts';
import { fair, storm } from '../sim/presets.ts';
import type { Conditions } from '../sim/scene.ts';
import { DEFAULT_INITIAL_SNOW, type InitialSnowParams } from '../sim/snowcover.ts';
import { snowFraction, type TemperatureProfile } from '../sim/temperature.ts';
import type { TimelineEvent } from '../sim/timeline.ts';
import type { WeakLayerGrain, WeakLayerSpec } from '../sim/weaklayers.ts';
import type { GtsCellSeries } from './gts.ts';
import type { VarsomForecast, VarsomProblem } from './varsom.ts';

/**
 * Turning observed / forecast data into a scenario: the seNorge snow depth at the start becomes
 * the existing snow (a depth-vs-elevation fit over the 1 km cells), the hourly seNorge weather
 * becomes a timeline of storm and fair events, and Varsom's persistent weak layers become weak
 * layers on the old snow surface. Pure — the fetching lives in state/realData.ts.
 */

export interface RealDataInput {
  /** Hour 0 of the timeline (UTC, on the hour). */
  start: Date;
  /** Hour (from `start`) to evaluate at. */
  evaluationHour: number;
  /** Terrain elevation range (m), the temperature profile's anchors. */
  minElevation: number;
  maxElevation: number;
  /** Snow depth (cm) and SWE (mm) per 1 km cell on the start day. */
  snowDepth: GtsCellSeries[];
  swe: GtsCellSeries[];
  /**
   * Hourly series per 1 km cell, index 0 = `start`: temperature (°C, instantaneous),
   * precipitation (mm in the hour ending at that time), wind speed (m/s) and direction (deg).
   */
  temperature: GtsCellSeries[];
  precipitation: GtsCellSeries[];
  windSpeed: GtsCellSeries[];
  windDirection: GtsCellSeries[];
  /** Cloud cover (%) for hour k = [start + k, start + k + 1); null where unknown. */
  cloudCover: (number | null)[] | null;
  varsom: VarsomForecast | null;
}

export interface RealDataSummary {
  hours: number;
  /** Fitted existing snow depth (cm) at the terrain's lowest and highest points. */
  snowLowCm: number;
  snowHighCm: number;
  /** Total precipitation (mm) at the storm reference elevation, over the whole timeline. */
  precipitationMm: number;
  events: number;
  weakLayers: number;
  warnings: string[];
}

/** Hours grouped into one block before events are formed (matches the fair-weather step). */
export const BLOCK_HOURS = 3;
/** A block is a storm if it brings at least this much precipitation (mm)… */
const STORM_PRECIP_MM = 0.5;
/** …or wind at or above this (m/s): enough to drift fresh snow. */
const STORM_WIND = 6;
/**
 * Consecutive blocks merge into one event while their weather stays this similar — and, for
 * fair weather, while the valley and the summit each stay on the same side of freezing (melt vs
 * refreeze matters more than a few degrees).
 */
const MERGE_TEMP_C = 3;
const MERGE_FAIR_TEMP_C = 4;
const MERGE_WIND_MS = 3;
const MERGE_WIND_DEG = 45;
const MAX_EVENT_HOURS = 24;
/** A block is clear if its mean cloud cover is below this (%). */
const CLEAR_SKY_PCT = 40;
/** Standard lapse rate (°C/m), for a regression with no elevation spread. */
const LAPSE = -0.0065;

/** Least-squares line y = a + b·x; `bDefault` if the xs don't spread. */
export function fitLine(
  xs: readonly number[],
  ys: readonly number[],
  bDefault = 0,
): { a: number; b: number } {
  const n = xs.length;
  if (n === 0) return { a: 0, b: bDefault };
  let mx = 0;
  let my = 0;
  for (let i = 0; i < n; i++) {
    mx += xs[i]!;
    my += ys[i]!;
  }
  mx /= n;
  my /= n;
  let sxx = 0;
  let sxy = 0;
  for (let i = 0; i < n; i++) {
    sxx += (xs[i]! - mx) ** 2;
    sxy += (xs[i]! - mx) * (ys[i]! - my);
  }
  const b = sxx > 1e-6 ? sxy / sxx : bDefault;
  return { a: my - b * mx, b };
}

/** Values of series `s` at time index `k` with the matching altitudes, skipping missing ones. */
function column(s: readonly GtsCellSeries[], k: number): { alt: number[]; v: number[] } {
  const alt: number[] = [];
  const v: number[] = [];
  for (const c of s) {
    const x = c.data[k];
    if (x === undefined || Number.isNaN(x)) continue;
    alt.push(c.altitude);
    v.push(x);
  }
  return { alt, v };
}

/** Existing snow from the seNorge cells: depth linear in elevation, bulk density from SWE. */
export function fitInitialSnow(
  depth: readonly GtsCellSeries[],
  swe: readonly GtsCellSeries[],
): InitialSnowParams {
  const d = column(depth, 0);
  const cells = d.alt.length;
  if (cells === 0) return { ...DEFAULT_INITIAL_SNOW, referenceDepth: 0 };
  const line = fitLine(
    d.alt,
    d.v.map((cm) => cm / 100),
  );
  const meanAlt = d.alt.reduce((s, x) => s + x, 0) / cells;
  let sweSum = 0;
  let depthSum = 0;
  for (const c of swe) {
    const sd = depth.find((x) => x.cellIndex === c.cellIndex)?.data[0];
    const w = c.data[0];
    if (sd === undefined || w === undefined || Number.isNaN(sd) || Number.isNaN(w) || sd < 5)
      continue;
    sweSum += w;
    depthSum += sd / 100;
  }
  const density = depthSum > 0 ? Math.min(550, Math.max(150, sweSum / depthSum)) : 300;
  return {
    ...DEFAULT_INITIAL_SNOW,
    referenceElevation: Math.round(meanAlt),
    referenceDepth: Math.max(0, line.a + line.b * meanAlt),
    // Snow deepens with elevation; a negative fit (wind, forest) would strip the summits bare.
    gradientPer100m: Math.max(0, line.b * 100),
    // The fitted line reaches zero on its own; no separate cut-off.
    snowlineElevation: 0,
    density: Math.round(density),
  };
}

interface Block {
  start: number;
  hours: number;
  low: number;
  high: number;
  /** Precipitation (mm) at the reference elevation, and its increase per 100 m (%). */
  precipRef: number;
  orographicPct: number;
  windSpeed: number;
  windFrom: number;
  cloud: number | null;
}

function vectorMean(speeds: number[], dirs: number[]): { speed: number; dir: number } {
  let u = 0;
  let v = 0;
  let s = 0;
  for (let i = 0; i < speeds.length; i++) {
    const r = (dirs[i]! * Math.PI) / 180;
    u += speeds[i]! * Math.sin(r);
    v += speeds[i]! * Math.cos(r);
    s += speeds[i]!;
  }
  const n = Math.max(1, speeds.length);
  const dir = ((Math.atan2(u, v) * 180) / Math.PI + 360) % 360;
  // Mean speed (not the vector mean's magnitude): drift depends on how hard it blows.
  return { speed: s / n, dir };
}

function angleDiff(a: number, b: number): number {
  const d = Math.abs(a - b) % 360;
  return d > 180 ? 360 - d : d;
}

/** Number of leading hours for which every hourly series has data. */
export function availableHours(input: RealDataInput): number {
  const series = [input.temperature, input.precipitation, input.windSpeed, input.windDirection];
  let hours = Infinity;
  for (const s of series) {
    let n = 0;
    const len = Math.max(0, ...s.map((c) => c.data.length));
    for (let k = 0; k < len; k++) {
      if (column(s, k).v.length === 0) break;
      n = k;
    }
    hours = Math.min(hours, n);
  }
  return Number.isFinite(hours) ? hours : 0;
}

function blocks(input: RealDataInput, hours: number, refElevation: number): Block[] {
  const out: Block[] = [];
  for (let h0 = 0; h0 + BLOCK_HOURS <= hours; h0 += BLOCK_HOURS) {
    let low = 0;
    let high = 0;
    let precipRef = 0;
    let precipMean = 0;
    let precipSlope = 0;
    const speeds: number[] = [];
    const dirs: number[] = [];
    let cloud = 0;
    let cloudN = 0;
    for (let h = h0; h < h0 + BLOCK_HOURS; h++) {
      // Temperature at the middle of the hour: mean of the two hour marks.
      for (const k of [h, h + 1]) {
        const t = column(input.temperature, k);
        const fit = fitLine(t.alt, t.v, LAPSE);
        low += (fit.a + fit.b * input.minElevation) / 2;
        high += (fit.a + fit.b * input.maxElevation) / 2;
      }
      // Precipitation, wind: the value stamped at the end of the hour.
      const p = column(input.precipitation, h + 1);
      const pFit = fitLine(p.alt, p.v);
      precipRef += Math.max(0, pFit.a + pFit.b * refElevation);
      const pm = p.v.length ? p.v.reduce((s, x) => s + x, 0) / p.v.length : 0;
      precipMean += pm;
      precipSlope += pFit.b;
      const ws = column(input.windSpeed, h + 1).v;
      const wd = column(input.windDirection, h + 1).v;
      const n = Math.min(ws.length, wd.length);
      const m = vectorMean(ws.slice(0, n), wd.slice(0, n));
      speeds.push(m.speed);
      dirs.push(m.dir);
      const c = input.cloudCover?.[h];
      if (c !== null && c !== undefined) {
        cloud += c;
        cloudN++;
      }
    }
    const wind = vectorMean(speeds, dirs);
    out.push({
      start: h0,
      hours: BLOCK_HOURS,
      low: low / BLOCK_HOURS,
      high: high / BLOCK_HOURS,
      precipRef,
      orographicPct: precipMean > 0.05 ? (precipSlope / precipMean) * 100 * 100 : 0,
      windSpeed: wind.speed,
      windFrom: wind.dir,
      cloud: cloudN > 0 ? cloud / cloudN : null,
    });
  }
  return out;
}

const isStorm = (b: Block) => b.precipRef >= STORM_PRECIP_MM || b.windSpeed >= STORM_WIND;
const isClear = (b: Block) => b.cloud !== null && b.cloud < CLEAR_SKY_PCT;

function profile(low: number, high: number, minE: number, maxE: number): TemperatureProfile {
  const r = (x: number) => Math.round(x * 10) / 10;
  return { lowElevation: minE, lowTemp: r(low), highElevation: maxE, highTemp: r(high) };
}

/** New snow (cm) at the reference elevation from precipitation (mm) there. */
function newSnowCm(precipMm: number, tRef: number, wind: number): number {
  return ((precipMm * snowFraction(tRef)) / newSnowDensity(tRef, wind)) * 100;
}

/** Group blocks into timeline events: runs of similar storm or fair blocks. */
export function eventsFromBlocks(
  bs: readonly Block[],
  minE: number,
  maxE: number,
  refElevation: number,
): TimelineEvent[] {
  const events: TimelineEvent[] = [];
  let run: Block[] = [];
  const flush = () => {
    if (run.length === 0) return;
    const hours = run.reduce((s, b) => s + b.hours, 0);
    const avg = (f: (b: Block) => number) => run.reduce((s, b) => s + f(b) * b.hours, 0) / hours;
    const t = profile(
      avg((b) => b.low),
      avg((b) => b.high),
      minE,
      maxE,
    );
    if (isStorm(run[0]!)) {
      const wind = vectorMean(
        run.map((b) => b.windSpeed),
        run.map((b) => b.windFrom),
      );
      // Snow per block with that block's temperature, so a storm turning to rain counts right.
      let cm = 0;
      let precip = 0;
      for (const b of run) {
        const tRef = b.low + ((b.high - b.low) * (refElevation - minE)) / Math.max(1, maxE - minE);
        cm += newSnowCm(b.precipRef, tRef, b.windSpeed);
        precip += b.precipRef;
      }
      const e = storm(
        hours,
        Math.round(cm * 10) / 10,
        Math.round(wind.speed * 10) / 10,
        Math.round(wind.dir),
        t,
      );
      e.referenceElevation = Math.round(refElevation);
      // Keep the measured amount: a storm that falls partly as rain isn't expressible in cm.
      e.measured = { precipitationMm: Math.round(precip * 10) / 10, newSnowCm: e.newSnowCm };
      e.orographicPctPer100m =
        precip > 0.1
          ? Math.round(
              Math.min(
                20,
                Math.max(
                  0,
                  avg((b) => b.orographicPct),
                ),
              ) * 10,
            ) / 10
          : 0;
      events.push(e);
    } else {
      events.push(fair(hours, isClear(run[0]!), t));
    }
    run = [];
  };
  for (const b of bs) {
    const prev = run[run.length - 1];
    const runHours = run.reduce((s, x) => s + x.hours, 0);
    const first = run[0];
    const tol = prev && isStorm(prev) ? MERGE_TEMP_C : MERGE_FAIR_TEMP_C;
    const similar =
      prev !== undefined &&
      first !== undefined &&
      isStorm(prev) === isStorm(b) &&
      runHours + b.hours <= MAX_EVENT_HOURS &&
      // Against the run's first block, so a slow drift doesn't merge a whole day's cooling.
      Math.abs(first.low - b.low) <= tol &&
      Math.abs(first.high - b.high) <= tol &&
      (isStorm(b)
        ? Math.abs(prev.windSpeed - b.windSpeed) <= MERGE_WIND_MS &&
          angleDiff(prev.windFrom, b.windFrom) <= MERGE_WIND_DEG
        : isClear(prev) === isClear(b) &&
          prev.low > 0 === b.low > 0 &&
          prev.high > 0 === b.high > 0);
    if (!similar) flush();
    run.push(b);
  }
  flush();
  return events;
}

/** Weak-layer grain and placement for a Varsom avalanche-problem cause, if it names one. */
export function weakLayerFromCause(
  cause: string,
): { grain: WeakLayerGrain; placement: 'surface' | 'ground' } | null {
  const c = cause.toLowerCase();
  if (c.includes('surface hoar')) return { grain: 'SH', placement: 'surface' };
  if (c.includes('depth hoar') || c.includes('near ground') || c.includes('near the ground'))
    return { grain: 'DH', placement: 'ground' };
  if (c.includes('facet')) return { grain: 'FC', placement: 'surface' };
  if (c.includes('graupel')) return { grain: 'PPgp', placement: 'surface' };
  return null;
}

export function weakLayersFromVarsom(problems: readonly VarsomProblem[]): WeakLayerSpec[] {
  const out: WeakLayerSpec[] = [];
  for (const p of problems) {
    const wl = weakLayerFromCause(p.causeName);
    if (!wl) continue;
    p.bands.forEach((band, k) => {
      out.push({
        id: `varsom-${p.typeId}-${out.length}-${k}`,
        enabled: true,
        grain: wl.grain,
        placement: wl.placement,
        depthCm: 0,
        thicknessCm: wl.grain === 'SH' ? 1 : wl.grain === 'DH' ? 20 : 5,
        minElevation: band.min,
        maxElevation: band.max,
        aspects: p.aspects.some(Boolean) ? [...p.aspects] : Array<boolean>(8).fill(true),
      });
    });
  }
  return out;
}

export function buildRealConditions(input: RealDataInput): {
  conditions: Conditions;
  summary: RealDataSummary;
} {
  const warnings: string[] = [];
  const initialSnow = fitInitialSnow(input.snowDepth, input.swe);
  const at = (e: number) =>
    Math.max(
      0,
      initialSnow.referenceDepth +
        ((e - initialSnow.referenceElevation) / 100) * initialSnow.gradientPer100m,
    );
  if (initialSnow.referenceDepth <= 0.01 && initialSnow.gradientPer100m <= 0.001)
    warnings.push('seNorge has no snow here at the start date.');

  let hours = availableHours(input);
  hours -= hours % BLOCK_HOURS;
  if (hours < input.evaluationHour)
    warnings.push('Weather data ends before the chosen time; evaluating at the last hour.');
  if (!input.cloudCover) warnings.push('No cloud cover data: all fair weather treated as cloudy.');

  // Storm amounts refer to high terrain, where the snow falls as snow for longest.
  const refElevation = Math.round(
    input.minElevation + 0.75 * (input.maxElevation - input.minElevation),
  );
  const bs = blocks(input, hours, refElevation);
  const events = eventsFromBlocks(bs, input.minElevation, input.maxElevation, refElevation);
  const weakLayers = input.varsom ? weakLayersFromVarsom(input.varsom.problems) : [];
  const evaluationHour = Math.min(hours, Math.max(0, input.evaluationHour));
  const evalEvent =
    events.find((_e, k) => {
      const end = events.slice(0, k + 1).reduce((s, x) => s + x.durationHours, 0);
      return evaluationHour < end;
    }) ?? events[events.length - 1];

  return {
    conditions: {
      temperature: evalEvent?.temperature ?? profile(0, -8, input.minElevation, input.maxElevation),
      initialSnow,
      weakLayers,
      events,
      evaluationHour,
    },
    summary: {
      hours,
      snowLowCm: Math.round(at(input.minElevation) * 100),
      snowHighCm: Math.round(at(input.maxElevation) * 100),
      precipitationMm: Math.round(bs.reduce((s, b) => s + b.precipRef, 0) * 10) / 10,
      events: events.length,
      weakLayers: weakLayers.length,
      warnings,
    },
  };
}
